// PersonalInfo.tsx
import React, { useEffect, useMemo, useState } from 'react';
// import axios from "axios";
import axios from "../lib/axios";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from 'react-router-dom';
// import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
// import { Calendar } from "@/components/ui/calendar";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
// import { CalendarIcon } from "lucide-react";
// import { cn } from "@/lib/utils";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from '../config';
import {
  stringOrEmpty,
  stringOrEmptyOptional,
  stringMandatory,
  panString,
  gstString,
  addressString,
  pincodeNumberString,
  udyamString,
} from "@/helpers/validations";
import { useLoader } from "../contexts/LoaderContext";
import { useJourney } from "@/contexts/JourneyContext";
import CallbackHelp from "@/components/CallbackHelp";
import AddressFields from "@/components/AddressFields";
import JourneyHeader from "@/components/JourneyHeader";


export const businessFormLoadSchema = z.object({
  business_name: stringOrEmpty("Business Name"),
  nature_of_business: stringOrEmpty("Nature of Business"),
  business_activity: stringOrEmpty("Business Activity"),
  business_role: stringOrEmpty("Role"),
  other_role: stringOrEmpty("Other Role"),
  gst_number: stringOrEmpty("GST Number"),
  date_of_incorporation: stringOrEmpty("Date of Incorporation"),
  company_pan_number: stringOrEmpty("Company PAN"),
  business_constitution_type: stringOrEmpty("Constitution"),

  // Registered address (5 fields)
  registered_address_line1: stringOrEmptyOptional("Registered Address Line 1"),
  registered_address_line2: stringOrEmptyOptional("Registered Address Line 2"),
  registered_address_line3: stringOrEmptyOptional("Registered City / Town"),
  registered_address_landmark: stringOrEmptyOptional("Registered Address Landmark"),
  registered_address_pincode: stringOrEmptyOptional("Registered Address Pincode"),

  // Business address (5 fields)
  business_address_line1: stringOrEmptyOptional("Business Address Line 1"),
  business_address_line2: stringOrEmptyOptional("Business Address Line 2"),
  business_address_line3: stringOrEmptyOptional("Business City / Town"),
  business_address_landmark: stringOrEmptyOptional("Business Address Landmark"),
  business_address_pincode: stringOrEmptyOptional("Business Address Pincode"),

  udyam_number: stringOrEmpty("Udyam Number"),
  cin_llpin_number: stringOrEmptyOptional("CIN / LLPIN"),
});

export const businessFormSubmitSchema = z
  .object({
    business_name: stringMandatory("Business Name"),
    nature_of_business: stringMandatory("Nature of Business"),
    business_activity: stringMandatory("Business Activity"),
    business_role: stringMandatory("Role"),
    other_role: z.string().trim().optional(),
    gst_number: gstString("GST Number"),
    date_of_incorporation: stringMandatory("Date of Incorporation"),
    company_pan_number: panString("Company PAN"),
    business_constitution_type: stringMandatory("Constitution"),

    // Registered address (5 fields)
    registered_address_line1: addressString("Registered Address Line 1"),
    registered_address_line2: stringMandatory("Registered Address Line 2"),
    registered_address_line3: stringMandatory("Registered City / Town"),
    registered_address_landmark: stringOrEmptyOptional("Registered Address Landmark"),
    registered_address_pincode: pincodeNumberString("Registered Address Pincode"),

    // Business address (5 fields)
    business_address_line1: addressString("Business Address Line 1"),
    business_address_line2: stringMandatory("Business Address Line 2"),
    business_address_line3: stringMandatory("Business City / Town"),
    business_address_landmark: stringOrEmptyOptional("Business Address Landmark"),
    business_address_pincode: pincodeNumberString("Business Address Pincode"),

    udyam_number: udyamString("Udyam Number"),
    cin_llpin_number: stringOrEmptyOptional("CIN / LLPIN"),
  })
  .refine(
    (data) => {
      if (data.business_role === "other" && !data.other_role?.trim()) return false;
      return true;
    },
    {
      message: "Specify Role when 'Other' is selected",
      path: ["other_role"],
    }
  );


const BUSINESS_ACTIVITY_OPTIONS: Record<string, string[]> = {
  // Agriculture: [
  //   "Crop and animal production, hunting and related service activities",
  //   "Forestry and logging",
  //   "Fishing and aquaculture",
  // ],
  // Manufacturing: [
  //   "Manufacture of food products",
  //   "Manufacture of beverages",
  //   "Manufacture of tobacco products",
  //   "Manufacture of textiles",
  //   "Manufacture of wearing apparel",
  //   "Manufacture of leather and related products",
  //   "Manufacture of wood and cork products (except furniture)",
  //   "Manufacture of paper and paper products",
  //   "Printing and reproduction of recorded media",
  //   "Manufacture of coke and refined petroleum products",
  //   "Manufacture of chemicals and chemical products",
  //   "Manufacture of pharmaceuticals and medicinal products",
  //   "Manufacture of rubber and plastics products",
  //   "Manufacture of other non-metallic mineral products",
  //   "Manufacture of basic metals",
  //   "Manufacture of fabricated metal products",
  //   "Manufacture of computer, electronic and optical products",
  //   "Manufacture of electrical equipment",
  //   "Manufacture of machinery and equipment n.e.c.",
  //   "Manufacture of motor vehicles, trailers and semi-trailers",
  //   "Manufacture of other transport equipment",
  //   "Manufacture of furniture",
  //   "Other manufacturing",
  //   "Repair and installation of machinery and equipment",
  // ],
  // Professional: [
  //   "Legal and accounting activities",
  //   "Management consultancy activities",
  //   "Architecture and engineering activities",
  //   "Scientific research and development",
  //   "Advertising and market research",
  //   "Other professional, scientific and technical activities",
  //   "Veterinary activities",
  //   "Human health activities",
  //   "Residential care activities",
  //   "Social work activities without accommodation",
  // ],
  // Salaried: [
  //   "Activities of households as employers of domestic personnel",
  //   "Undifferentiated goods and services producing activities of households",
  // ],
  Services: [
    // "Mining of coal and lignite",
    // "Extraction of crude petroleum and natural gas",
    // "Mining of metal ores",
    // "Other mining and quarrying",
    // "Mining support service activities",
    // "Electricity, gas, steam and air conditioning supply",
    // "Water collection, treatment and supply",
    // "Sewerage",
    // "Waste collection, treatment and disposal; materials recovery",
    // "Remediation and other waste management services",
    // "Construction of buildings",
    // "Civil engineering",
    // "Specialized construction activities",
    // "Accommodation",
    "Food and beverage service activities",
    // "Publishing activities",
    // "Motion picture, video and television programme production",
    // "Programming and broadcasting activities",
    // "Telecommunications",
    // "Computer programming, consultancy and related activities",
    // "Information service activities",
    // "Financial service activities",
    // "Insurance, reinsurance and pension funding",
    // "Other financial activities",
    // "Real estate activities",
    // "Rental and leasing activities",
    // "Employment activities",
    // "Travel agency and tour operator activities",
    // "Security and investigation activities",
    // "Services to buildings and landscape activities",
    // "Office administrative and business support activities",
    // "Public administration and defence",
    // "Education",
    // "Creative, arts and entertainment activities",
    // "Libraries, archives, museums and cultural activities",
    // "Gambling and betting activities",
    // "Sports, amusement and recreation activities",
    // "Activities of membership organizations",
    // "Repair of computers and personal goods",
    // "Other personal service activities",
    // "Activities of extraterritorial organizations and bodies",
  ],
  // Traders: [
  //   "Wholesale and retail trade and repair of motor vehicles",
  //   "Wholesale trade (except motor vehicles)",
  //   "Retail trade (except motor vehicles)",
  // ],
};

type FormValues = z.infer<typeof businessFormSubmitSchema>;

const BusinessInfo = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [shouldContactOwner, setShouldContactOwner] = useState(false);
  const [sameAsRegistered, setSameAsRegistered] = useState(false);
  const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
  const [activitySearch, setActivitySearch] = useState("");
  const { loading, setLoading } = useLoader();
  const { refresh: refreshJourney } = useJourney();
  const [isBusinessNameEditable, setIsBusinessNameEditable] = useState(false);

  const form = useForm<z.infer<typeof businessFormSubmitSchema>>({
    resolver: zodResolver(businessFormSubmitSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      business_name: "",
      nature_of_business: "",
      business_activity: "",
      business_role: "",
      other_role: "",
      gst_number: "",
      date_of_incorporation: "",
      company_pan_number: "",
      business_constitution_type: "",
      registered_address_line1: "",
      registered_address_line2: "",
      registered_address_line3: "",
      registered_address_landmark: "",
      registered_address_pincode: "",
      business_address_line1: "",
      business_address_line2: "",
      business_address_line3: "",
      business_address_landmark: "",
      business_address_pincode: "",
      udyam_number: "",
      cin_llpin_number: "",
    },
  });



  React.useEffect(() => {
    const subscription = form.watch((values) => {
      const comm = {
        line1: values.registered_address_line1?.trim() || "",
        line2: values.registered_address_line2?.trim() || "",
        line3: values.registered_address_line3?.trim() || "",
        landmark: values.registered_address_landmark?.trim() || "",
        pincode: values.registered_address_pincode?.trim() || "",
      };

      const res = {
        line1: values.business_address_line1?.trim() || "",
        line2: values.business_address_line2?.trim() || "",
        line3: values.business_address_line3?.trim() || "",
        landmark: values.business_address_landmark?.trim() || "",
        pincode: values.business_address_pincode?.trim() || "",
      };

      const allMatch =
        comm.line1 === res.line1 &&
        comm.line2 === res.line2 &&
        comm.line3 === res.line3 &&
        comm.landmark === res.landmark &&
        comm.pincode === res.pincode &&
        Object.values(comm).some((v) => v !== ""); // avoid matching empty addresses

      setSameAsRegistered(allMatch);
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    setError,
    getValues,
    formState: { errors },
  } = form;

  // The onSubmit function that you define to process the data after validation
  const onSubmit = (data: FormValues) => {
    setLoading(true);
    console.log("Form data", data);  // This is where you handle the data when form is valid
    axios
      .post(`${API_BASE_URL}/api/set_business_details/`, data, { maxRedirects: 0 })
      .then((res) => {
        console.log("Backend response:", res.data);
        console.log("Status:", res.data.status);
        console.log("Message:", res.data.message);

        // toast({
        //   title: "Information saved",
        //   description:
        //     <div>
        //       "Status : "{res.data.status} <br />
        //       "Message : "{res.data.message}
        //     </div>
        // });
        console.log(res.status);
        console.log(res.statusText);
        if (res.status === 200) {
          // Refresh journey state to get the updated stage after API advances it
          refreshJourney().then(() => {
            // Small delay to ensure state has propagated to JourneySync
            setTimeout(() => {
              if (res.data.next_page_url) {
                navigate(res.data.next_page_url);
              } else {
                navigate("/additional-details");
              }
            }, 150);
          }).catch(() => {
            // If refresh fails, navigate anyway
            setTimeout(() => {
              if (res.data.next_page_url) {
                navigate(res.data.next_page_url);
              } else {
                navigate("/additional-details");
              }
            }, 150);
          });
        }
        else if (res.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
          navigate("/session-timeout");
        }
        // if (res.status === 200 && res.data.status === "success") {
        //   navigate("/personal-info");
        // } else if (res.status === 200 && res.data.status === "failure") {
        //   navigate("/contact-owner");
        // } else if (res.status === 200 && res.data.status === "pending") {
        //   navigate("/additional-details");
        // } else {
        //   navigate("/waiting-for-updates");
        // }

      }).catch((err) => {
        if (confirm("Error saving data: " + err.message + ".\n Press OK to reload the page.")) {
          window.location.reload();
        }
      })
      .finally(() => setLoading(false));
  };


  React.useEffect(() => {
    if (sameAsRegistered) {
      const reg = {
        line1: form.watch("registered_address_line1"),
        line2: form.watch("registered_address_line2"),
        line3: form.watch("registered_address_line3"),
        landmark: form.watch("registered_address_landmark"),
        pincode: form.watch("registered_address_pincode"),
      };

      form.setValue("business_address_line1", reg.line1 ?? "");
      form.setValue("business_address_line2", reg.line2 ?? "");
      form.setValue("business_address_line3", reg.line3 ?? "");
      form.setValue("business_address_landmark", reg.landmark ?? "");
      form.setValue("business_address_pincode", reg.pincode ?? "");
    }
  }, [
    sameAsRegistered,
    form.watch("registered_address_line1"),
    form.watch("registered_address_line2"),
    form.watch("registered_address_line3"),
    form.watch("registered_address_landmark"),
    form.watch("registered_address_pincode"),
  ]);



  // Fetching data from backend and setting it to the form
  useEffect(() => {
    const loadBusinessDetails = async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API_BASE_URL}/api/get_business_details/`, {}, { maxRedirects: 0 });

        console.log(res.status, res.statusText);
        if (res.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
          navigate("/session-timeout");
          return;
        }
        console.log("Backend response:", res.data);

        type LoadValues = z.infer<typeof businessFormLoadSchema>;

        if (!res.data.business_name || res.data.business_name === 'WHITESPACE') {
          setIsBusinessNameEditable(true);
        }

        // 1. [DISABLED] trigger_udhyam_fetch auto-call has been disabled.
        //    Udyam number will be filled manually by the user if not already present.
        // if (!serverData.udyam_number) {
        //   console.log("[udhyam] udyam_number empty on load — calling trigger_udhyam_fetch...");
        //   try {
        //     const triggerRes = await axios.post(`${API_BASE_URL}/api/trigger_udhyam_fetch/`, {});
        //     console.log("[udhyam] trigger result:", triggerRes.data);
        //     if (triggerRes.data?.urn) {
        //       serverData = { ...serverData, udyam_number: triggerRes.data.urn };
        //     }
        //   } catch (triggerErr) {
        //     console.warn("[udhyam] trigger_udhyam_fetch failed (non-blocking):", triggerErr);
        //   }
        // }
        const serverData = res.data;

        // 2. Validate response against Load Schema
        const loadResult = businessFormLoadSchema.safeParse(serverData);
        if (!loadResult.success) {
          const flatErrors = loadResult.error.flatten();
          Object.entries(flatErrors.fieldErrors).forEach(([field, messages]) => {
            if (messages?.length) {
              setError(field as keyof LoadValues, { type: "manual", message: messages[0] });
            }
          });
          return;
        }

        // 3. Reset form with clean data
        const parsedLoadData = loadResult.data;

        const regAddr = {
          line1: parsedLoadData.registered_address_line1?.trim() || "",
          line2: parsedLoadData.registered_address_line2?.trim() || "",
          line3: parsedLoadData.registered_address_line3?.trim() || "",
          landmark: parsedLoadData.registered_address_landmark?.trim() || "",
          pincode: parsedLoadData.registered_address_pincode?.trim() || "",
        };

        const bizAddr = {
          line1: parsedLoadData.business_address_line1?.trim() || "",
          line2: parsedLoadData.business_address_line2?.trim() || "",
          line3: parsedLoadData.business_address_line3?.trim() || "",
          landmark: parsedLoadData.business_address_landmark?.trim() || "",
          pincode: parsedLoadData.business_address_pincode?.trim() || "",
        };

        const areSame =
          regAddr.line1 === bizAddr.line1 &&
          regAddr.line2 === bizAddr.line2 &&
          regAddr.line3 === bizAddr.line3 &&
          regAddr.landmark === bizAddr.landmark &&
          regAddr.pincode === bizAddr.pincode;

        setSameAsRegistered(areSame);
        reset(parsedLoadData);

        // Wait for RHF to finish updating formState
        await new Promise((resolve) => setTimeout(resolve, 0));
        console.log("Form errors after load:", form.formState.errors);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBusinessDetails();
  }, []);




  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const business_role = form.watch("business_role");
  const nature_of_business = form.watch("nature_of_business");

  // Reset business_activity when nature_of_business changes
  useEffect(() => {
    const currentActivity = form.getValues("business_activity");
    const validOptions = BUSINESS_ACTIVITY_OPTIONS[nature_of_business] || [];
    if (currentActivity && !validOptions.includes(currentActivity)) {
      form.setValue("business_activity", "");
    }
  }, [nature_of_business, form]);

  useEffect(() => {
    if (business_role !== "other") {
      // Clear the value
      form.setValue("other_role", "");

      // Optionally clear validation errors
      form.clearErrors("other_role");
    } else {
      // Trigger validation if needed when showing
      form.trigger("other_role");
    }
  }, [business_role, form]);

  return (
    <div className="min-h-screen bg-intellend-white overflow-x-hidden">
      {/* Compact header */}
      <JourneyHeader />

      {/* Main content */}
      <main className="container mx-auto px-3 py-4 max-w-2xl">
        <div className="bg-white shadow-md rounded-lg p-4 mb-4 overflow-hidden">
          <h2 className="text-xl font-bold text-intellend-green mb-4">Business Information</h2>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <fieldset className="border border-gray-300 p-4 rounded-md space-y-4 min-w-0">
                <legend className="font-bold">Business Details</legend>

                {/* 1. Business Name */}
                <FormField
                  control={form.control}
                  name="business_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">Name of Business<span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Business name"
                          {...field}
                          className="h-9"
                          disabled={!isBusinessNameEditable}
                          showSuccess={!!field.value && !form.formState.errors.business_name}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Nature Of Business */}
                <FormField
                  control={form.control}
                  name="nature_of_business"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">Nature of Business<span className="text-red-500">*</span></FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""} // ✅ controlled, updates on reset
                      >
                        <FormControl>
                          <SelectTrigger className="h-9" showSuccess={!!field.value && !form.formState.errors.nature_of_business}>
                            <SelectValue placeholder="Nature of Business" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {/* <SelectItem value="AGRICULTURE">AGRICULTURE</SelectItem> */}
                          {/* <SelectItem value="Manufacturing">Manufacturing</SelectItem> */}
                          {/* <SelectItem value="Professional">Professional</SelectItem> */}
                          {/* <SelectItem value="Salaried">Salaried</SelectItem> */}
                          <SelectItem value="Services">Services</SelectItem>
                          {/* <SelectItem value="Trading">Trading</SelectItem> */}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Business Activity - Bottom Sheet Selector */}
                <FormField
                  control={form.control}
                  name="business_activity"
                  render={({ field }) => {
                    const options = BUSINESS_ACTIVITY_OPTIONS[nature_of_business] || [];
                    const filtered = options.filter((a) =>
                      a.toLowerCase().includes(activitySearch.toLowerCase())
                    );
                    return (
                      <FormItem>
                        <FormLabel className="text-sm">Business Activity<span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <div className="relative w-full">
                            <button
                              type="button"
                              disabled={!nature_of_business}
                              onClick={() => { setActivitySearch(""); setIsActivityDrawerOpen(true); }}
                              className={`flex h-9 w-full min-w-0 items-center justify-between overflow-hidden rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${field.value && !form.formState.errors.business_activity ? "pr-10" : ""
                                }`}
                            >
                              <span className={`truncate ${field.value ? "text-foreground" : "text-muted-foreground"}`}>
                                {field.value || (nature_of_business ? "Select Business Activity" : "Select Nature of Business first")}
                              </span>
                              <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                            </button>
                            {field.value && !form.formState.errors.business_activity && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                <div className="bg-intellend-green rounded-full p-0.5">
                                  <Check className="h-3 w-3 text-white stroke-[4px]" />
                                </div>
                              </div>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />

                        <Drawer open={isActivityDrawerOpen} onOpenChange={setIsActivityDrawerOpen}>
                          <DrawerContent className="max-h-[85vh]">
                            <DrawerHeader className="pb-2">
                              <DrawerTitle>Select Business Activity</DrawerTitle>
                            </DrawerHeader>

                            {/* Search Input */}
                            <div className="px-4 pb-3">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  placeholder="Search activities..."
                                  value={activitySearch}
                                  onChange={(e) => setActivitySearch(e.target.value)}
                                  className="h-9 pl-9 pr-8"
                                />
                                {activitySearch && (
                                  <button
                                    type="button"
                                    onClick={() => setActivitySearch("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Options List */}
                            <div className="overflow-y-auto px-2 pb-4" style={{ maxHeight: "60vh" }}>
                              {filtered.length === 0 ? (
                                <p className="text-center text-sm text-muted-foreground py-6">No activities found</p>
                              ) : (
                                filtered.map((activity) => (
                                  <button
                                    key={activity}
                                    type="button"
                                    onClick={() => {
                                      field.onChange(activity);
                                      setIsActivityDrawerOpen(false);
                                    }}
                                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors text-left ${field.value === activity
                                      ? "bg-green-50 text-green-700 font-medium"
                                      : "hover:bg-muted"
                                      }`}
                                  >
                                    <span className="flex-1">{activity}</span>
                                    {field.value === activity && (
                                      <Check className="h-4 w-4 text-green-600 shrink-0" />
                                    )}
                                  </button>
                                ))
                              )}
                            </div>
                          </DrawerContent>
                        </Drawer>
                      </FormItem>
                    );
                  }}
                />

                {/* 2. Role + GST */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="business_role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">Your Role<span className="text-red-500">*</span></FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""} // ✅ controlled, updates on reset
                        >
                          <FormControl>
                            <SelectTrigger className="h-9" showSuccess={!!field.value && !form.formState.errors.business_role}>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="beneficialOwner">Beneficial Owner</SelectItem>
                            <SelectItem value="partner">Partner</SelectItem>
                            <SelectItem value="director">Director</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="other_role"
                    render={({ field }) => (
                      <FormItem className={business_role !== "other" ? "hidden" : ""}>
                        <FormLabel className="text-sm">
                          Specify Role<span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Your role"
                            {...field}
                            value={field.value || ""}
                            className="h-9"
                            showSuccess={!!field.value && !form.formState.errors.other_role}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />



                  <FormField
                    control={form.control}
                    name="gst_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">GST Number<span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input
                            maxLength={15}
                            placeholder="15-digit GSTIN"
                            {...field}
                            className="uppercase h-9"
                            showSuccess={!!field.value && !form.formState.errors.gst_number}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* 3. DOI + PAN */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="date_of_incorporation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">Date of Incorporation<span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            className="h-9"
                            showSuccess={!!field.value && !form.formState.errors.date_of_incorporation}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="company_pan_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">Business PAN<span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input
                            placeholder="10-digit PAN"
                            maxLength={10}
                            {...field}
                            className="uppercase h-9"
                            showSuccess={!!field.value && !form.formState.errors.company_pan_number}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* 4. Constitution */}
                <FormField
                  control={form.control}
                  name="business_constitution_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">Constitution<span className="text-red-500">*</span></FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""} // ✅ controlled, updates on reset
                      >
                        <FormControl>
                          <SelectTrigger className="h-9" showSuccess={!!field.value && !form.formState.errors.business_constitution_type}>
                            <SelectValue placeholder="Business type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="soleProprietorship">Sole Proprietorship</SelectItem>
                          <SelectItem value="llp">LLP</SelectItem>
                          <SelectItem value="privateLimited">Private Limited</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <fieldset className="border border-gray-300 rounded p-4 space-y-4">
                  <legend className="font-bold">Registered Address</legend>
                  {/* Registered Address */}
                  <AddressFields
                    form={form}
                    prefix="registered"
                    requiredFlags={[true, true, true, false, true]}
                  />
                </fieldset>
                <fieldset className="border border-gray-300 rounded p-4 space-y-4">
                  <legend className="font-bold">
                    Shop / Business Address
                  </legend>
                  <div className="text-sm text-red-600 border border-red-300 bg-red-50 rounded-md px-3 py-1 inline-block">
                    Verification will be done at this address
                  </div>                                  {/* Same as checkbox remains unchanged */}
                  <label className="flex items-center gap-2 text-sm pt-2">
                    <input
                      type="checkbox"
                      checked={sameAsRegistered}
                      onChange={(e) => {
                        setSameAsRegistered(e.target.checked);
                        if (e.target.checked) {
                          form.setValue("business_address_line1", form.watch("registered_address_line1"));
                          form.setValue("business_address_line2", form.watch("registered_address_line2"));
                          form.setValue("business_address_line3", form.watch("registered_address_line3"));
                          form.setValue("business_address_landmark", form.watch("registered_address_landmark"));
                          form.setValue("business_address_pincode", form.watch("registered_address_pincode"));
                          form.trigger([
                            "business_address_line1",
                            "business_address_line2",
                            "business_address_line3",
                            "business_address_landmark",
                            "business_address_pincode",
                          ]);
                        }
                      }}
                    />
                    Same as Registered Address
                  </label>

                  {/* Business Address */}
                  <AddressFields
                    form={form}
                    prefix="business"
                    requiredFlags={[true, true, true, false, true]}
                    disabled={sameAsRegistered}
                  />
                </fieldset>
                {/* 7. Udyam & CIN */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="udyam_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">Udyam Number<span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. UDYAM-XX-00-0000000"
                            {...field}
                            className="uppercase h-9"
                            showSuccess={!!field.value && !form.formState.errors.udyam_number}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cin_llpin_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">CIN / LLPIN</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Optional"
                            {...field}
                            className="uppercase h-9"
                            showSuccess={!!field.value && !form.formState.errors.cin_llpin_number}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </fieldset>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCallbackOpen(true)}
                  className="w-full sm:w-auto h-9"
                >
                  Request Callback
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:w-auto sm:ml-auto bg-intellend-green hover:bg-intellend-green/90 h-9"
                  disabled={loading || !form.formState.isValid}
                >
                  Next
                </Button>
              </div>
            </form>

          </Form>
        </div>
      </main>

      <CallbackHelp
        isOpen={isCallbackOpen}
        setIsOpen={setIsCallbackOpen}
        showOk={false}
        showCallUs={true}
        showWhatsappUs={true}
        silentlyTrigger={true}
        title="Intellend Support"
      />

    </div>
  );
};

export default BusinessInfo;